import { Link } from 'react-router-dom';
import { Zap, Mail, Phone, MapPin, Github, Twitter, Linkedin } from 'lucide-react';
import { useSettings } from '../../context/SettingsContext';

export default function Footer() {
  const { t } = useSettings();
  return (
    <footer style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
      <div className="container footer-container" style={{ padding: '72px 28px 40px' }}>
        <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr repeat(3, 1fr)', gap: 48, marginBottom: 56 }}>
          {/* Brand */}
          <div className="footer-brand">
            <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'linear-gradient(135deg, var(--accent), var(--gold))', borderRadius: 10, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Zap size={18} color="white" fill="white" />
              </div>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>Tech<span style={{ color: 'var(--accent)' }}>Store</span></span>
            </Link>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.75, maxWidth: 280, marginBottom: 24 }}>
              Your premier destination for professional electronics. Quality products, competitive prices, expert support.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              {[Twitter, Github, Linkedin].map((Icon, i) => (
                <a key={i} href="#"
                  style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--bg-card)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'var(--transition)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-dim)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-card)'; }}>
                  <Icon size={16} color="var(--text-secondary)" />
                </a>
              ))}
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 20 }}>{t('footer', 'shop')}</h4>
            {['Laptops', 'Smartphones', 'Audio', 'Accessories', 'Cameras', 'Gaming'].map(cat => (
              <Link key={cat} to={`/products?category=${cat}`}
                style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 14, marginBottom: 10, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}>{cat}</Link>
            ))}
          </div>

          {/* Account */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 20 }}>{t('footer', 'account')}</h4>
            {[
              [t('nav', 'profile'), '/profile'],
              [t('nav', 'orders'), '/orders'],
              [t('nav', 'cart'), '/cart'],
              [t('nav', 'contact'), '/contact'],
              [t('nav', 'login'), '/login'],
            ].map(([label, path]) => (
              <Link key={path} to={path}
                style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 14, marginBottom: 10, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}>{label}</Link>
            ))}
          </div>

          {/* Contact */}
          <div>
            <h4 style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 20 }}>{t('footer', 'contact')}</h4>
            {[
              [Mail, 'support@techstore.com', 'mailto:support@techstore.com'],
              [Phone, '+216 74 000 000', 'tel:+21674000000'],
              [MapPin, 'Sfax, Tunisia', null],
            ].map(([Icon, text, href]) => (
              <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, background: 'var(--accent-dim)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon size={15} color="var(--accent)" />
                </div>
                {href ? (
                  <a href={href} style={{ color: 'var(--text-secondary)', fontSize: 14, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.target.style.color = 'var(--accent)'}
                    onMouseLeave={e => e.target.style.color = 'var(--text-secondary)'}>{text}</a>
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{text}</span>
                )}
              </div>
            ))}
            <Link to="/contact" className="btn btn-primary btn-sm" style={{ marginTop: 8, display: 'inline-flex' }}>
              {t('nav', 'contact')}
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>© {new Date().getFullYear()} TechStore. {t('footer', 'rights')}</p>
          <div style={{ display: 'flex', gap: 20 }}>
            {[['Privacy Policy', '#'], ['Terms of Service', '#'], ['Sitemap', '#']].map(([label, href]) => (
              <a key={label} href={href} style={{ color: 'var(--text-muted)', fontSize: 13, transition: 'color 0.2s' }}
                onMouseEnter={e => e.target.style.color = 'var(--text-primary)'}
                onMouseLeave={e => e.target.style.color = 'var(--text-muted)'}>{label}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
